import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, PlayCircle, ShieldCheck, ArrowRight, BookOpen, Clock, Calendar, Heart, Film, Users, MessageCircle, ChevronRight } from 'lucide-react';
import { collection, query, getDocs, orderBy, where, limit } from 'firebase/firestore';
import { getDb } from '../lib/firebase';
import LoadingSpinner from '../components/LoadingSpinner';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';
import { MOCK_POSTS } from '../data/mockPosts';

const PILLARS = [
  { id: 'all', label: 'All Posts', icon: null, accent: '#FFBF00' },
  { id: 'grief-comfort', label: 'Grief & Comfort', icon: Heart, accent: '#D85A30' },
  { id: 'memory-legacy', label: 'Memory & Legacy', icon: Film, accent: '#1D9E75' },
  { id: 'funeral-guidance', label: 'Funeral Guidance', icon: ShieldCheck, accent: '#185FA5' },
  { id: 'culture', label: 'Culture & Traditions', icon: BookOpen, accent: '#BA7517' },
  { id: 'conversation', label: 'Faraja Conversations', icon: PlayCircle, accent: '#7F77DD' },
  { id: 'behind-scenes', label: 'Behind the Scenes', icon: Users, accent: '#C44536' },
];

// Image placeholder for posts without a featured image
function ImagePlaceholder({ category, title }: { category: string; title: string }) {
  const pillar = PILLARS.find(c => c.id === category) || PILLARS[0];
  const categoryAccent = pillar.accent;
  const Icon = pillar.icon || PlayCircle;
  const hash = title.split('').reduce((a, b) => a + b.charCodeAt(0), 0);
  const pattern = hash % 3;

  return (
    <div className="w-full h-full bg-[#0a0a0a] flex flex-col items-center justify-center relative overflow-hidden">
      {/* Pattern 0: two radial gradients + 3 concentric circle borders */}
      {pattern === 0 && (
        <>
          <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 20% 20%, ${categoryAccent}, transparent 60%), radial-gradient(circle at 80% 80%, ${categoryAccent}, transparent 60%)` }} />
          {[1, 2, 3].map(i => (
            <div key={i} className="absolute rounded-full border border-white" style={{ width: `${i * 30}%`, height: `${i * 30}%`, opacity: i === 1 ? 0.12 : i === 2 ? 0.08 : 0.05 }} />
          ))}
        </>
      )}
      {/* Pattern 1: linear-gradient diagonal + 4 vertical rule lines */}
      {pattern === 1 && (
        <>
          <div className="absolute inset-0 opacity-10" style={{ background: `linear-gradient(135deg, ${categoryAccent}, transparent)` }} />
          <div className="absolute inset-0 flex justify-around px-12 opacity-10">
            {[1, 2, 3, 4].map(i => <div key={i} className="w-px h-full bg-white" />)}
          </div>
        </>
      )}
      {/* Pattern 2: radial gradient top + 2 nested rounded-[2rem] border boxes */}
      {pattern === 2 && (
        <>
          <div className="absolute inset-0 opacity-15" style={{ background: `radial-gradient(circle at 50% 0%, ${categoryAccent}, transparent 70%)` }} />
          <div className="absolute inset-8 rounded-[2rem] border border-white/10" />
          <div className="absolute inset-16 rounded-[2rem] border border-white/5" />
        </>
      )}

      {/* Centred icon + category */}
      <div className="relative z-10 flex flex-col items-center opacity-40">
        <Icon size={40} className="mb-3" />
        <span className="text-[10px] font-bold uppercase tracking-[0.2em]">{pillar.label}</span>
      </div>

      {/* Bottom center branding */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/15 text-[8px] uppercase tracking-[0.25em] whitespace-nowrap">
        FARAJA MEDIA
      </div>
    </div>
  );
}

// Fallback posts for when Firestore is empty or loading failed

// FIX: safe date formatter — handles missing or invalid publishDate
function formatDate(publishDate: any): string {
  try {
    if (!publishDate) return '';
    const d = typeof publishDate.toDate === 'function' ? publishDate.toDate() : new Date(publishDate);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString();
  } catch {
    return '';
  }
}

export default function Blog() {
  const [posts, setPosts] = useState<any[]>([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [conversationPosts, setConversationPosts] = useState<any[]>([]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        // FIX: use simple query without composite index requirement; sort client-side
        const snap = await getDocs(query(
          collection(getDb(), 'blog_posts'),
          where('status', '==', 'published')
        ));
        const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Merge in MOCK_POSTS (What Families Regret series + guides)
        MOCK_POSTS.forEach((mockPost) => {
          if (!all.find((p: any) => p.slug === mockPost.slug)) {
            all.push(mockPost);
          }
        });

        // Sort by publishDate descending client-side (safe when some docs lack publishDate)
        all.sort((a: any, b: any) => {
          const at = a.publishDate?.toMillis?.() ?? 0;
          const bt = b.publishDate?.toMillis?.() ?? 0;
          return bt - at;
        });
        setPosts(all);

        // Conversations strip — also sorted client-side
        const conv = all.filter((p: any) => p.category === 'conversation').slice(0, 3);
        setConversationPosts(conv);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = posts.filter(post => {
    const matchesFilter = filter === 'all' || post.category === filter;
    const matchesSearch = (post.title ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (post.excerpt ?? '').toLowerCase().includes(search.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const featuredPost = posts.find(post => post.featured === true);
  const otherPosts = filteredPosts.filter(post => post.id !== featuredPost?.id);
  const blogSchema = {
    "@type": "CollectionPage",
    "name": "Faraja Media Journal",
    "description": "Stories, guidance, grief support, and cultural remembrance articles from Faraja Media.",
    "url": "https://farajamedia.com/blog",
    "mainEntity": {
      "@type": "Blog",
      "name": "Faraja Media Journal",
      "blogPost": posts.slice(0, 10).map((post) => ({
        "@type": "BlogPosting",
        "headline": post.seoTitle ?? post.title,
        "url": `https://farajamedia.com/blog/${post.slug}`,
        ...(post.excerpt ? { "description": post.excerpt } : {}),
        ...(post.publishDate?.toDate ? { "datePublished": post.publishDate.toDate().toISOString() } : {})
      }))
    }
  };

  if (loading) {
    return (
      <div className="bg-bg min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-bg pt-32 pb-48 selection:bg-gold/30 selection:text-gold">
      <SEO
        title="Journal | Faraja Media"
        description="East Africa's hub for grief, legacy, and cultural remembrance."
        schema={blogSchema}
      />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 text-center mb-24">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-5xl md:text-[100px] font-serif mb-10 leading-[0.9] tracking-tight text-ink"
        >
          Stories of <em>memory,</em><br />loss & legacy.
        </motion.h1>

        {/* Pillar filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {PILLARS.map(p => (
            <button
              key={p.id}
              onClick={() => setFilter(p.id)}
              className={cn(
                "px-6 py-3 rounded-full text-[10px] font-bold uppercase tracking-widest border transition-all flex items-center gap-2",
                filter === p.id
                  ? "bg-gold border-gold text-bg"
                  : "bg-white/5 border-white/5 text-ink3 hover:border-gold/50"
              )}
            >
              {p.icon && <p.icon size={14} />}
              {p.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-ink3 w-4 h-4" />
          <input
            type="text"
            placeholder="Search stories..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search blog posts"
            className="w-full bg-white/5 border border-white/10 rounded-full py-3 pl-11 pr-4 text-ink placeholder:text-ink3 focus:outline-none focus:border-gold/50 transition-colors"
          />
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section className="max-w-7xl mx-auto px-6 mb-24">
          <Link to={`/blog/${featuredPost.slug}`} className="group block">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 hover:border-gold/30 transition-all">
              <div className="absolute top-0 left-0 right-0 h-1 z-10" style={{ background: PILLARS.find(p => p.id === featuredPost.category)?.accent ?? '#FFBF00' }} />
              <div className="grid md:grid-cols-2 gap-0">
                <div className="aspect-video md:aspect-auto md:h-full overflow-hidden relative">
                  {featuredPost.image ? (
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      referrerPolicy="no-referrer"
                      loading="lazy"
                    />
                  ) : (
                    <ImagePlaceholder title={featuredPost.title ?? ''} category={featuredPost.category} />
                  )}
                  {featuredPost.youtubeUrl && (
                    <div className="absolute bottom-4 left-4 bg-black/70 backdrop-blur px-3 py-1 rounded-full text-xs flex items-center gap-1 text-gold">
                      <PlayCircle size={12} /> Watch interview
                    </div>
                  )}
                </div>
                <div className="p-8 md:p-12 flex flex-col justify-center bg-white/[0.02]">
                  <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-gold">
                      {PILLARS.find(p => p.id === featuredPost.category)?.label ?? featuredPost.category}
                    </span>
                    {featuredPost.guestName && (
                      <span className="text-[9px] font-bold uppercase tracking-widest text-ink3">with {featuredPost.guestName}</span>
                    )}
                  </div>
                  <h2 className="text-3xl md:text-5xl font-serif text-ink mb-4 group-hover:text-gold transition-colors">
                    {featuredPost.title}
                  </h2>
                  <p className="text-ink3 text-lg mb-6 line-clamp-3">{featuredPost.excerpt}</p>
                  <div className="flex items-center gap-4 text-sm text-ink3 flex-wrap">
                    {formatDate(featuredPost.publishDate) && (
                      <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(featuredPost.publishDate)}</span>
                    )}
                    <span className="flex items-center gap-1"><Clock size={14} /> {featuredPost.readTime ?? '5 min read'}</span>
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </section>
      )}

      {/* Faraja Conversations Strip */}
      {conversationPosts.length > 0 && filter === 'all' && (
        <section className="max-w-7xl mx-auto px-6 mb-24">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-serif text-ink">Faraja Conversations</h3>
              <p className="text-ink3 text-sm">In-depth interviews with voices on grief, memory & legacy</p>
            </div>
            <Link to="/faraja-conversations" className="text-gold text-sm flex items-center gap-1 hover:gap-2 transition-all">
              View all <ChevronRight size={14} />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {conversationPosts.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                <div className="card-default overflow-hidden border border-white/5 hover:border-gold/30 transition-all rounded-xl">
                  <div className="aspect-video overflow-hidden relative bg-black">
                    {post.image ? (
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" referrerPolicy="no-referrer" loading="lazy" />
                    ) : (
                      <ImagePlaceholder title={post.title ?? ''} category="conversation" />
                    )}
                    <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-all flex items-center justify-center">
                      <PlayCircle size={48} className="text-gold/80 group-hover:text-gold transition-all" />
                    </div>
                  </div>
                  <div className="p-5">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-gold">Episode {post.episodeNumber ?? '—'}</span>
                    <h4 className="text-xl font-serif text-ink mt-2 mb-1 group-hover:text-gold transition-colors">{post.title}</h4>
                    {post.guestName && <p className="text-sm text-ink3">with {post.guestName}</p>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Post Grid */}
      <section className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {otherPosts.map((post) => {
            const pillar = PILLARS.find(p => p.id === post.category) ?? PILLARS[0];
            return (
              <Link key={post.id} to={`/blog/${post.slug}`} className="group">
                <div className="card-default overflow-hidden flex flex-col border border-white/5 hover:border-gold/30 transition-all rounded-xl">
                  <div className="h-1" style={{ background: pillar.accent }} />
                  <div className="aspect-video overflow-hidden relative bg-black">
                    {post.image ? (
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                        loading="lazy"
                      />
                    ) : (
                      <ImagePlaceholder title={post.title ?? ''} category={post.category} />
                    )}
                    {post.youtubeUrl && (
                      <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur rounded-full p-1.5">
                        <PlayCircle size={16} className="text-gold" />
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-[9px] font-bold uppercase tracking-widest text-gold">{pillar.label}</span>
                      {post.guestName && <span className="text-[9px] text-ink3">with {post.guestName}</span>}
                    </div>
                    <h3 className="text-xl font-serif text-ink mb-3 group-hover:text-gold transition-colors line-clamp-2">{post.title}</h3>
                    <p className="text-ink3 text-sm line-clamp-2 mb-4">{post.excerpt}</p>
                    <div className="mt-auto flex items-center justify-between text-xs text-ink3">
                      {formatDate(post.publishDate) && (
                        <span className="flex items-center gap-1"><Calendar size={12} /> {formatDate(post.publishDate)}</span>
                      )}
                      <span className="flex items-center gap-1"><Clock size={12} /> {post.readTime ?? '4 min read'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {otherPosts.length === 0 && (
          <div className="text-center py-20">
            <p className="text-ink3">No posts found. Check back soon for new stories.</p>
          </div>
        )}
      </section>

      {/* Newsletter Section */}
      <section className="max-w-6xl mx-auto px-6 mt-32">
        <div className="bg-white/[0.01] rounded-[4rem] overflow-hidden border border-white/5 grid lg:grid-cols-2 items-center shadow-2xl relative group">
          <div className="absolute top-0 left-0 w-full h-full bg-gold/5 opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
          <div className="aspect-square lg:aspect-auto h-full overflow-hidden relative">
            <img
              src="https://images.pixieset.com/492772111/4587df0377dcad806d0cfc3d6d06d800-xxlarge.jpg"
              alt="Faraja Conversations — Bible and Flowers"
              className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-1000"
              referrerPolicy="no-referrer"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-bg via-transparent to-transparent hidden lg:block" />
          </div>
          <div className="p-12 md:p-24 text-center lg:text-left relative z-10">
            <h2 className="text-4xl md:text-5xl font-serif mb-6 text-ink">Is your family going through this right now?</h2>
            <p className="text-ink3 text-lg mb-4 leading-relaxed">
              You do not have to navigate it alone. We document final chapters with care, dignity, and deep respect — funeral coverage across Kenya and live streaming for diaspora families worldwide.
            </p>
            <p className="text-ink3/60 italic text-sm mb-12">When you are ready to talk — we are here.</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/254721296564"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-10 py-4 shadow-xl shadow-gold/10 flex items-center justify-center gap-2"
              >
                <MessageCircle size={20} /> Talk to us on WhatsApp
              </a>
              <Link to="/booking" className="px-10 py-4 rounded-xl border border-white/10 text-ink3 hover:bg-white/5 transition flex items-center justify-center">
                Request Coverage
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}